import React, { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import './CustomBottomNav.css';
import GuestLoginModal from '../../GuestLoginModal';
import { AuthContext } from '../../AuthContext/AuthContext';

const navItems = [
  { key: 'home',  icon: <HomeIcon />,               route: '/dashboard',     label: 'Home' },
  { key: 'list',  icon: <FormatListBulletedIcon />, route: '/lease_details', label: 'List' },
  { key: 'saves', icon: <FavoriteBorderIcon />,     route: '/lease_save',    label: 'Saves' },
  { key: 'inbox', icon: <MailOutlineIcon />,        route: '/lease-inboxlist',     label: 'Inbox' },
];

// ✅ Map tab — shown on /lease_details (replaces List)
const LEASE_MAP_ITEM = {
  key: 'map',
  icon: <MapIcon />,
  route: '/lease_map',
  label: 'Map',
};

// ✅ List tab — shown on /lease_map (replaces List back)
const LEASE_LIST_ITEM = {
  key: 'list',
  icon: <FormatListBulletedIcon />,
  route: '/lease_details',
  label: 'List',
};

const CustomBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentKey, setCurrentKey] = useState('');
  const navRef = useRef(null);
  const { isGuest } = useContext(AuthContext);
  const [showGuestModal, setShowGuestModal] = useState(false);

  const guestRestrictedTabs = ["saves", "inbox"];

  // ✅ Detect the two pages
  const isLeaseDetailsPage = location.pathname.startsWith('/lease_details');
  const isLeaseMapPage = location.pathname.startsWith('/lease_map');

  // ✅ Build active nav items based on page:
  //   /lease_details → Map tab  (swap list → map)
  //   /lease_map     → List tab (swap list → list, keeps key "list" but routes to /lease_details)
  const activeNavItems = isLeaseDetailsPage
    ? navItems.map(item => (item.key === 'list' ? LEASE_MAP_ITEM : item))
    : isLeaseMapPage
      ? navItems.map(item => (item.key === 'list' ? LEASE_LIST_ITEM : item))
      : navItems;

  // ── Active key logic ───────────────────────────────────────────────────
  useEffect(() => {
    // On /lease_map → highlight "list" tab
    if (isLeaseMapPage) {
      setCurrentKey('list');
      return;
    }

    // On /lease_details → highlight "map" tab
    if (isLeaseDetailsPage) {
      setCurrentKey('map');
      return;
    }

    const activeItem =
      activeNavItems.find((item) => location.pathname.startsWith(item.route))?.key ||
      'home';
    setCurrentKey(activeItem);
  }, [location.pathname, isLeaseDetailsPage, isLeaseMapPage]);

  const handleNav = (item) => {
    // 🚫 Block guest access
    if (isGuest && guestRestrictedTabs.includes(item.key)) {
      setShowGuestModal(true);
      return;
    }

    setCurrentKey(item.key);

    // ✅ Map tab → /lease_map
    if (item.key === 'map') {
      navigate('/lease_map');
      return;
    }

    // ✅ List tab on /lease_map → /lease_details
    if (item.key === 'list' && isLeaseMapPage) {
      navigate('/lease_details');
      return;
    }

    navigate(item.route);
  };

  // ── Indicator position ─────────────────────────────────────────────────
  useEffect(() => {
    const current = navRef.current?.querySelector(`.nav-item[data-key="${currentKey}"]`);
    const indicator = navRef.current?.querySelector('.nav-indicator');
    if (current && indicator) {
      const { offsetLeft, offsetWidth } = current;
      indicator.style.left = `${offsetLeft + offsetWidth / 2 - 25}px`;
    }
  }, [currentKey, isLeaseDetailsPage, isLeaseMapPage]);

  return (
    <div className="nav-wrapper">
      <div className="nav-bar" ref={navRef}>
        {activeNavItems.map((item) => (
          <div
            key={item.key}
            data-key={item.key}
            className={`nav-item ${currentKey === item.key ? 'nav-item-active' : ''}`}
            onClick={() => handleNav(item)}
          >
            {item.icon}
            <div className="nav-label">{item.label}</div>
          </div>
        ))}
        <div className="nav-indicator" />
      </div>

      <GuestLoginModal
        open={showGuestModal}
        onClose={() => setShowGuestModal(false)}
      />
    </div>
  );
};

export default CustomBottomNav;